import { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

/* Severity / Status badge colours */
const SEVERITY_VARIANT = {
 Critical: "danger",
 High: "danger",
 Medium: "warning",
 Low: "success",
};

const STATUS_VARIANT = {
 Open: "danger",
 "In Progress": "warning",
 Resolved: "success",
};

/* Severity row-highlight class */
const ROW_CLASS = {
 Critical: "table-danger",
 High: "table-danger",
 Medium: "table-warning",
 Low: "table-success",
};

const HindranceRegister = () => {
 const [searchParams] = useSearchParams();

 const [issues, setIssues] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [projects, setProjects] = useState([]);
 const [loading, setLoading] = useState(false);
 const [exporting, setExporting] = useState(false);

 const [filters, setFilters] = usePersistentFilters("hindrance_register_filters", {
 workOrder: searchParams.get("workOrder") || "",
 project: searchParams.get("project") || "",
 fromDate: "",
 toDate: "",
 severity: "",
 status: "",
 });

 /* Summary counters */
 const counts = issues.reduce(
 (acc, iss) => {
 acc.total++;
 acc[iss.severity] = (acc[iss.severity] || 0) + 1;
 acc[iss.status] = (acc[iss.status] || 0) + 1;
 return acc;
 },
 { total: 0 }
 );

 /* Load dropdown data */
 useEffect(() => {
 const loadDropdowns = async () => {
 try {
 const [woRes, prRes] = await Promise.all([
 api.get("/work-orders?limit=1000"),
 api.get("/projects?limit=1000"),
 ]);
 if (woRes.data.success) setWorkOrders(woRes.data.data || []);
 if (prRes.data.success) setProjects(prRes.data.data || []);
 } catch {
 /* silent — dropdowns not critical */
 }
 };
 loadDropdowns();
 }, []);

 /* Filter work orders by selected project */
 const filteredWorkOrders = filters.project
    ? workOrders.filter(wo => String(wo.project?._id || wo.project) === String(filters.project))
    : workOrders;

 /* Fetch hindrance data */
 useEffect(() => {
 fetchIssues();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [filters]);

 const fetchIssues = async () => {
 try {
 setLoading(true);

 /* Pull DPRs that have issues — reuse the existing DPR list API */
 const params = {
 limit: 1000,
 ...(filters.workOrder && { workOrder: filters.workOrder }),
 ...(filters.project && { project: filters.project }),
 ...(filters.fromDate && { fromDate: filters.fromDate }),
 ...(filters.toDate && { toDate: filters.toDate }),
 };

 const res = await api.get("/dpr", { params });

 console.log("Fetched DPRs with issues:", res.data.data);

 if (!res.data.success) return;

 /* Flatten issue details */
 const flat = [];
 for (const dpr of res.data.data) {
 console.log("DPR:", dpr.dprNumber, "issueDetails:", dpr.issueDetails);
 if (!dpr.issueDetails?.length) continue;
 for (const iss of dpr.issueDetails) {
 if (filters.severity && iss.severity !== filters.severity) continue;
 if (filters.status && iss.status !== filters.status) continue;
 flat.push({
 dprId: dpr._id,
 dprNumber: dpr.dprNumber,
 date: dpr.date,
 resolvedDate: iss.resolvedDate || (iss.status === "Resolved" ? dpr.updatedAt : null),
 workOrder: dpr.workOrder?.woNo || dpr.workOrder || "",
 activities: (dpr.activitiesCompleted || [])
 .map(a => a.boqName || a.activity || "")
 .filter(Boolean)
 .join(", "),
 issue: iss.issue || "",
 description: iss.description || "",
 severity: iss.severity || "Low",
 status: iss.status || "Open",
 resolutionRemarks: iss.resolutionRemarks || iss.actionTaken || "",
 remarks: dpr.remarks || "",
 });
 }
 }

 setIssues(flat);
 console.log("Flattened hindrance issues:", flat);
 } catch {
 swal("Error", "Failed to load hindrance data", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project") {
        if (value && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(value)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (value) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(value));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

 /* Excel download */
 const handleExportExcel = async () => {
 try {
 setExporting(true);

 const params = new URLSearchParams();
 if (filters.project) params.append("project", filters.project);
 if (filters.workOrder) params.append("workOrder", filters.workOrder);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);

 const res = await api.get(`/dpr/hindrance-excel?${params.toString()}`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;

 /* Extract filename from header if present */
 const cd = res.headers?.["content-disposition"] || "";
 const match = cd.match(/filename="?([^"]+)"?/);
 link.download = match?.[1] || "Hindrance_Register.xlsx";

 link.click();
 window.URL.revokeObjectURL(url);

 swal("Success!", "Hindrance Register downloaded successfully", "success");
 } catch {
 swal("Error", "Failed to export Excel report", "error");
 } finally {
 setExporting(false);
 }
 };

 console.log("Hindrance Register render — issues:", issues, "filters:", filters);

 /* Severity badge */
 const SeverityBadge = ({ sev }) => (
 <Badge bg={SEVERITY_VARIANT[sev] || "secondary"}>{sev}</Badge>
 );

 const StatusBadge = ({ st }) => (
 <Badge bg={STATUS_VARIANT[st] || "secondary"}>{st}</Badge>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Hindrance Register" motherMenu="DPR" />

 {/* Summary Cards */}
 <Row className="mb-3 g-3">
 {[
 { label: "Total Hindrances", value: counts.total, color: "#1F3864", icon: "fa-exclamation-triangle", bg: "#EEF2FF" },
 { label: "Critical", value: counts.Critical || 0, color: "#dc3545", icon: "fa-fire", bg: "#FFF5F5" },
 { label: "High", value: counts.High || 0, color: "#e85d04", icon: "fa-arrow-up", bg: "#FFF3EE" },
 { label: "Medium", value: counts.Medium || 0, color: "#f4a261", icon: "fa-minus-circle", bg: "#FFFBF0" },
 { label: "Low", value: counts.Low || 0, color: "#2a9d8f", icon: "fa-check-circle", bg: "#F0FDF9" },
 { label: "Open", value: counts.Open || 0, color: "#c0392b", icon: "fa-folder-open", bg: "#FFF5F5" },
 { label: "In Progress", value: counts["In Progress"] || 0, color: "#e67e22", icon: "fa-spinner", bg: "#FFFBF0" },
 { label: "Resolved", value: counts.Resolved || 0, color: "#27ae60", icon: "fa-check-double", bg: "#F0FDF4" },
 ].map((s, i) => (
 <Col key={i} xs={6} sm={4} md={3} lg={3} xl={true}>
 <div
 style={{
 background: s.bg,
 borderRadius: 12,
 padding: "14px 16px",
 borderTop: `4px solid ${s.color}`,
 display: "flex",
 alignItems: "center",
 gap: 12,
 minWidth: 0,
 height: "100%",
 }}
 >
 <div
 style={{
 width: 40,
 height: 40,
 borderRadius: "50%",
 background: s.color,
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 flexShrink: 0,
 }}
 >
 <i className={`fas ${s.icon}`} style={{ color: "#fff", fontSize: 15 }} />
 </div>
 <div style={{ minWidth: 0 }}>
 <div style={{ fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>
 {s.value}
 </div>
 <div style={{ fontSize: 11, color: "#6c757d", fontWeight: 500, marginTop: 2, whiteSpace: "nowrap" }}>
 {s.label}
 </div>
 </div>
 </div>
 </Col>
 ))}
 </Row>

 {/* Filters + Export */}
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Hindrance Register &amp; Delay Monitoring</Card.Title>
 <Button
 variant="success"
 size="sm"
 onClick={handleExportExcel}
 disabled={exporting}
 >
 {exporting
 ? <><Spinner size="sm" className="me-1" />Exporting…</>
 : <><i className="fas fa-file-excel me-2" />Export Excel</>
 }
 </Button>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 {/* Project */}
 <div className="d-flex flex-column" style={{ width: 180 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">Project</label>
 <Form.Select
 size="sm"
 value={filters.project}
 onChange={e => handleFilterChange("project", e.target.value)}
 >
 <option value="">All Projects</option>
 {projects.map(p => (
 <option key={p._id} value={p._id}>
 {p.projectCode ? `${p.projectCode} — ` : ""}{p.projectName}
 </option>
 ))}
 </Form.Select>
 </div>

 {/* Work Order */}
 <div className="d-flex flex-column" style={{ width: 200 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">Work Order</label>
 <Form.Select
 size="sm"
 value={filters.workOrder}
 onChange={e => handleFilterChange("workOrder", e.target.value)}
 >
 <option value="">All Work Orders</option>
 {filteredWorkOrders.map(wo => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </div>

 {/* From Date */}
 <div className="d-flex flex-column" style={{ width: 140 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">From Date</label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.fromDate}
 onChange={e => handleFilterChange("fromDate", e.target.value)}
 />
 </div>

 {/* To Date */}
 <div className="d-flex flex-column" style={{ width: 140 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">To Date</label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.toDate}
 onChange={e => handleFilterChange("toDate", e.target.value)}
 />
 </div>

 {/* Severity */}
 <div className="d-flex flex-column" style={{ width: 120 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">Severity</label>
 <Form.Select
 size="sm"
 value={filters.severity}
 onChange={e => handleFilterChange("severity", e.target.value)}
 >
 <option value="">All</option>
 <option value="Critical">Critical</option>
 <option value="High">High</option>
 <option value="Medium">Medium</option>
 <option value="Low">Low</option>
 </Form.Select>
 </div>

 {/* Status */}
 <div className="d-flex flex-column" style={{ width: 130 }}>
 <label className="text-muted mb-1 fs-12 fw-bold">Status</label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={e => handleFilterChange("status", e.target.value)}
 >
 <option value="">All Statuses</option>
 <option value="Open">Open</option>
 <option value="In Progress">In Progress</option>
 <option value="Resolved">Resolved</option>
 </Form.Select>
 </div>

 {/* Reset */}
 <div className="d-flex flex-column justify-content-end">
 <Button
 variant="outline-secondary"
 size="sm"
 onClick={() =>
 setFilters({ workOrder: "", project: "", fromDate: "", toDate: "", severity: "", status: "" })
 }
 >
 Reset
 </Button>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <Spinner variant="primary" />
 </div>
 ) : issues.length === 0 ? (
 <div className="text-center py-5">
 <h5 className="text-muted">No hindrances found for selected filters</h5>
 </div>
 ) : (
  <div className="table-responsive">
  <Table hover bordered size="sm" className="align-middle">
  <thead className="table-dark">
  <tr>
  <th style={{ width: 45 }}>#</th>
  <th>DPR No.</th>
  <th>Date Reported</th>
  <th>Work Order</th>
  <th>Activity / Scope</th>
  <th>Hindrance Description</th>
  <th>Severity</th>
  <th>Status</th>
  <th>Date Resolved</th>
  <th>Action Taken / Resolution Remarks</th>
  </tr>
  </thead>
  <tbody>
  {issues.map((iss, idx) => (
  <tr key={`${iss.dprId}-${idx}`} className={ROW_CLASS[iss.severity] || ""}>
  <td className="text-center">{idx + 1}</td>
  <td>
  <strong>{iss.dprNumber}</strong>
  </td>
  <td>
  {iss.date
  ? new Date(iss.date).toLocaleDateString("en-IN", {
  day: "2-digit", month: "short", year: "numeric",
  })
  : "—"}
  </td>
  <td>{iss.workOrder || "—"}</td>
  <td>{iss.activities || "—"}</td>
  <td>
    <div className="fw-bold">{iss.issue}</div>
    {iss.description && (
      <small className="text-muted d-block mt-1">{iss.description}</small>
    )}
  </td>
  <td><SeverityBadge sev={iss.severity} /></td>
  <td><StatusBadge st={iss.status} /></td>
  <td>
  {iss.resolvedDate
  ? (
    <span className="text-success fw-semibold">
      {new Date(iss.resolvedDate).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })}
    </span>
  )
  : (
    <span className="text-muted">—</span>
  )}
  </td>
  <td>
    {iss.resolutionRemarks ? (
      <span className="text-success">{iss.resolutionRemarks}</span>
    ) : iss.remarks ? (
      <span className="text-muted">{iss.remarks}</span>
    ) : (
      <span className="text-muted">—</span>
    )}
  </td>
  </tr>
  ))}
  </tbody>
  </Table>
  </div>
 )}
 </Card.Body>
 </Card>
 </Fragment>
 );
};

export default HindranceRegister;
